import { useContext, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { TaxIdTypeMessages } from '../../../../enums/tax-identifiers/tax-identifiers-types';
import { groupByTaxIdType } from '../../../../services/tax-identifier-service';
import { PillBank } from '../../../pill-bank';
import { MultiCheckDropdown } from '../../../multi-check-dropdown';
import { CustomsContext } from '../../context/context';
import { messages } from '../messages';
import { useFulfillmentPlanTaxIdentifiers } from './use-fulfillment-plan-tax-identifiers';
import type { TaxIdentifier } from '../../types';

const NO_TAX_IDS_SAVED = 'NO_TAX_IDS_SAVED';
const SOME_TAX_IDS_SAVED = 'SOME_TAX_IDS_SAVED';
const LOADING_TAX_IDS = 'LOADING_TAX_IDS';
const SAVING_TAX_IDS = 'SAVING_TAX_IDS';

type TaxIdentifierDropdownProps = {
  sellerTaxIdentifiers: TaxIdentifier[];
};

export const TaxIdentifierDropdown: React.FC<TaxIdentifierDropdownProps> = ({ sellerTaxIdentifiers }) => {
  const intl = useIntl();
  const { fulfillmentPlanId } = useContext(CustomsContext);
  const { selectedTaxIdentifierIds, updateFpTaxIdentifiers, loading, saving } = useFulfillmentPlanTaxIdentifiers(fulfillmentPlanId);

  const sellerTaxIdentifierDisplayMap = Object.fromEntries(
    sellerTaxIdentifiers.map((value) => [value.taxIdentifierId, `${value.name} (${value.redactedTaxNumber})`]),
  );
  const selectableTaxIdGroups = groupByTaxIdType(sellerTaxIdentifiers);
  const activeSelectedTaxIdentifierIds = selectedTaxIdentifierIds.filter((taxId: string) =>
    Object.keys(sellerTaxIdentifierDisplayMap).includes(taxId),
  );

  const onUpdateTaxIds = (taxIdentifierIds: string[]) => {
    updateFpTaxIdentifiers(taxIdentifierIds);
  };

  const placeholderDisplayValues = {
    NO_TAX_IDS_SAVED: intl.formatMessage(messages.addFirstFPTaxIdentifiers),
    SOME_TAX_IDS_SAVED: intl.formatMessage(messages.addOtherFPTaxIdentifiers),
    LOADING_TAX_IDS: intl.formatMessage(messages.loadingFPTaxIdentifiers),
    SAVING_TAX_IDS: intl.formatMessage(messages.savingFPTaxIdentifiers),
  };

  const getTaxIdTypeLabel = (taxIdType: string) => {
    const messageDescriptor = TaxIdTypeMessages[taxIdType as keyof typeof TaxIdTypeMessages];
    if (!messageDescriptor) {
      return taxIdType;
    }
    return intl.formatMessage(messageDescriptor);
  };

  const renderTaxIdLabel = (itemId: string) => {
    return sellerTaxIdentifierDisplayMap[itemId];
  };

  const placeholderOptionId = useMemo(() => {
    if (loading) {
      return LOADING_TAX_IDS;
    }
    if (saving) {
      return SAVING_TAX_IDS;
    }
    if (activeSelectedTaxIdentifierIds.length < 1) {
      return NO_TAX_IDS_SAVED;
    }
    return SOME_TAX_IDS_SAVED;
  }, [loading, saving, activeSelectedTaxIdentifierIds]);

  return (
    <>
      <MultiCheckDropdown
        disabled={loading || saving}
        dropdownMenuShouldHaveWidthOfButton
        isManualApply
        items={selectableTaxIdGroups}
        onUpdate={onUpdateTaxIds}
        renderItemLabel={renderTaxIdLabel}
        renderSectionLabel={getTaxIdTypeLabel}
        selectedItems={selectedTaxIdentifierIds}
        toggleLabel={placeholderDisplayValues[placeholderOptionId]}
      />
      <PillBank labelFormatter={renderTaxIdLabel} onUpdate={onUpdateTaxIds} selectedItems={activeSelectedTaxIdentifierIds} />
    </>
  );
};
